import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSite } from '../content/SiteContext';

const ScrollToTopButton = () => {
  const { content, t } = useSite();
  const label = t(content.footer.backToTop);
  const [isVisible, setIsVisible] = useState(true); // Cambiado a true para prueba

  useEffect(() => {
    const toggleVisibility = () => {
      // Mostrar el botón cuando hayas scrolleado más de 1000px
      // Esto debería aparecer cuando llegues a QuoteSection
      const scrollPosition = window.scrollY;
      setIsVisible(scrollPosition > 1000);
    };

    // Verificar al cargar la página
    toggleVisibility();
    
    // Agregar event listener para scroll
    window.addEventListener('scroll', toggleVisibility);
    
    // Cleanup
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    // Animación rápida y visible hacia arriba
    const startPosition = window.pageYOffset;
    const startTime = performance.now();
    const duration = 600; // 600ms para que sea rápido pero visible

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Función de easing para una animación más dinámica y visible
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const newPosition = startPosition * (1 - easeOutQuart);
      window.scrollTo(0, newPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-[9999] w-16 h-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black border border-gray-600/40 rounded-full shadow-2xl backdrop-blur-lg hover:from-gray-800 hover:via-gray-700 hover:to-gray-900 transition-all duration-300 group"
          initial={{ opacity: 0, scale: 0, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0, y: 20 }}
          whileHover={{ 
            scale: 1.1,
            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)"
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 20 
          }}
          aria-label={label}
        >
          {/* Icono de flecha hacia arriba más grande */}
          <motion.svg
            className="w-7 h-7 text-gray-300 group-hover:text-white transition-colors duration-300 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            initial={{ y: 2 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </motion.svg>
          
          {/* Efecto de brillo sutil */}
          <motion.div
            className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
          />
          
          {/* Tooltip */}
          <motion.div
            className="absolute right-20 top-1/2 transform -translate-y-1/2 px-3 py-2 bg-gray-900/95 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            initial={{ opacity: 0, x: 10 }}
            whileHover={{ opacity: 1, x: 0 }}
          >
            {label}
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-900/95 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
          </motion.div>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ScrollToTopButton;
